/**
 * ExternalContactsTab Component
 * External contacts management - list and add form
 */

import React, { useState } from 'react';
import { Edit2, Trash2, UserPlus, Mail, Phone } from 'lucide-react';
import type { ExternalContact, AddContactFormData } from '../types';
import { CONTACT_ROLES } from '../types';
import { fieldCls } from '../../ui';

interface ExternalContactsTabProps {
  contacts: ExternalContact[];
  loading: boolean;
  onAddContact: (data: AddContactFormData) => Promise<unknown>;
  onUpdateContact: (contactId: string, data: Partial<ExternalContact>) => Promise<unknown>;
  onRemoveContact: (contactId: string) => Promise<unknown>;
}

export function ExternalContactsTab({
  contacts,
  loading,
  onAddContact,
  onUpdateContact,
  onRemoveContact,
}: ExternalContactsTabProps) {
  const [formData, setFormData] = useState<AddContactFormData>({
    first_name: '',
    last_name: '',
    role: '',
    email: '',
    phone: '',
    secondary_phone: '',
    company: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<ExternalContact>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.role) return;

    setSaving(true);
    setError(null);
    try {
      await onAddContact(formData);
      setFormData({
        first_name: '',
        last_name: '',
        role: '',
        email: '',
        phone: '',
        secondary_phone: '',
        company: '',
        notes: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (contact: ExternalContact) => {
    setEditingId(contact.id);
    setEditData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      role: contact.role,
      email: contact.email || '',
      phone: contact.phone || '',
      secondary_phone: contact.secondary_phone || '',
      company: contact.company || '',
      notes: contact.notes || '',
    });
  };

  const handleSaveEdit = async (contactId: string) => {
    try {
      await onUpdateContact(contactId, editData);
      setEditingId(null);
      setEditData({});
    } catch (err: any) {
      console.error('Error updating contact:', err);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left: Contacts list */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">External Contacts</h3>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-lg h-16 animate-pulse" />
            ))}
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <UserPlus size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No external contacts yet</p>
            <p className="text-sm">Add CRAs, monitors, and sponsors</p>
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Role / Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Phone</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {contacts.map((contact) => (
                  <tr key={contact.id}>
                    <td className="px-4 py-3">
                      {editingId === contact.id ? (
                        <div className="space-y-2">
                          <select
                            value={editData.role}
                            onChange={e => setEditData(prev => ({ ...prev, role: e.target.value }))}
                            className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-full"
                          >
                            {CONTACT_ROLES.map(role => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editData.first_name}
                              onChange={e => setEditData(prev => ({ ...prev, first_name: e.target.value }))}
                              placeholder="First"
                              className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-1/2"
                            />
                            <input
                              type="text"
                              value={editData.last_name}
                              onChange={e => setEditData(prev => ({ ...prev, last_name: e.target.value }))}
                              placeholder="Last"
                              className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-1/2"
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{contact.role}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">
                            {contact.first_name} {contact.last_name}
                          </div>
                          {contact.company && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{contact.company}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === contact.id ? (
                        <input
                          type="email"
                          value={editData.email || ''}
                          onChange={e => setEditData(prev => ({ ...prev, email: e.target.value }))}
                          className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-full"
                        />
                      ) : contact.email ? (
                        <a
                          href={`mailto:${contact.email}`}
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          <Mail size={12} />
                          {contact.email}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === contact.id ? (
                        <div className="space-y-2">
                          <input
                            type="tel"
                            value={editData.phone || ''}
                            onChange={e => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="Primary phone"
                            className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-full"
                          />
                          <input
                            type="tel"
                            value={editData.secondary_phone || ''}
                            onChange={e => setEditData(prev => ({ ...prev, secondary_phone: e.target.value }))}
                            placeholder="Secondary phone"
                            className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-full"
                          />
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {contact.phone && (
                            <a
                              href={`tel:${contact.phone}`}
                              className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-gray-100"
                            >
                              <Phone size={12} />
                              {contact.phone}
                            </a>
                          )}
                          {contact.secondary_phone && (
                            <a
                              href={`tel:${contact.secondary_phone}`}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <Phone size={10} />
                              {contact.secondary_phone}
                            </a>
                          )}
                          {!contact.phone && !contact.secondary_phone && (
                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {editingId === contact.id ? (
                          <>
                            <button
                              onClick={() => handleSaveEdit(contact.id)}
                              className="px-2 py-1 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-150"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingId(null);
                                setEditData({});
                              }}
                              className="px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors duration-150"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(contact)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                              title="Edit"
                            >
                              <Edit2 size={14} className="text-gray-400 dark:text-gray-500" />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Delete this contact?')) {
                                  onRemoveContact(contact.id);
                                }
                              }}
                              className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150"
                              title="Delete"
                            >
                              <Trash2 size={14} className="text-red-400" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Right: Add form */}
      <div className="w-full lg:w-80 lg:flex-shrink-0">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Add Contact</h4>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={e => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className={`w-full ${fieldCls}`}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={e => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className={`w-full ${fieldCls}`}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                value={formData.role}
                onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className={`w-full ${fieldCls}`}
                required
              >
                <option value="">Select a role...</option>
                {CONTACT_ROLES.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company (optional)
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={e => setFormData(prev => ({ ...prev, company: e.target.value }))}
                placeholder="e.g., Pfizer, IQVIA"
                className={`w-full ${fieldCls}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email (optional)
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className={`w-full ${fieldCls}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone (optional)
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className={`w-full ${fieldCls}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Secondary Phone (optional)
              </label>
              <input
                type="tel"
                value={formData.secondary_phone}
                onChange={e => setFormData(prev => ({ ...prev, secondary_phone: e.target.value }))}
                className={`w-full ${fieldCls}`}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={saving || !formData.first_name || !formData.last_name || !formData.role}
              className="w-full inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {saving ? 'Adding...' : 'Add Contact'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ExternalContactsTab;
